// Paso "Estamos verificando tu pago", compartido por /pay y /subscribe.
//
// Aparece cuando hay un pago del widget que todavía no pudimos confirmar: se
// agotó el sondeo después del aviso de éxito, o la página se recargó con un
// pago en curso guardado (ver lib/checkout-resume.ts).
//
// La acción principal es VERIFICAR el mismo pago, nunca crear otro. Pagar de
// nuevo existe, pero como salida explícita y apagada: antes el botón principal
// de esta situación era "Reintentar el pago", que descartaba un pago
// posiblemente aprobado y cobraba dos veces.

import { useEffect, useRef } from 'react';
import AuthShell from '@/components/AuthShell';
import SubmitButton from '@/components/SubmitButton';
import { btn } from '@/lib/button-styles';
import { DO_NOT_REPAY_MESSAGE } from '@/lib/checkout-outcome';

// Chequeo de fondo mientras la pantalla está abierta: si el cobro se aprueba,
// el usuario sigue solo sin tener que apretar nada.
const BACKGROUND_INTERVAL_MS = 10_000;
const BACKGROUND_MAX_CHECKS = 12;

interface PaymentVerifyingProps {
  /** 'resume': pago retomado tras recargar. 'timeout': se agotó el sondeo. */
  origin: 'resume' | 'timeout';
  /** Vuelve a sondear el MISMO pago. */
  onVerify: () => void;
  /** Salida explícita: recién acá se habilita un checkout nuevo. */
  onPayAgain: () => void;
  /** Consulta silenciosa periódica; solo actúa si hay un veredicto definitivo. */
  onBackgroundCheck?: () => void;
}

export default function PaymentVerifying({
  origin,
  onVerify,
  onPayAgain,
  onBackgroundCheck,
}: PaymentVerifyingProps): JSX.Element {
  // El callback cambia en cada render del padre: el intervalo lee siempre el
  // último sin reiniciarse.
  const checkRef = useRef(onBackgroundCheck);
  checkRef.current = onBackgroundCheck;

  useEffect(() => {
    let checks = 0;
    const id = setInterval(() => {
      checks += 1;
      if (checks >= BACKGROUND_MAX_CHECKS) clearInterval(id);
      checkRef.current?.();
    }, BACKGROUND_INTERVAL_MS);
    return () => clearInterval(id);
  }, []);

  return (
    <AuthShell title="Estamos verificando tu pago" subtitle="Puede demorar unos minutos.">
      <div className="space-y-4">
        <div role="status" className="rounded-lg border border-primary/30 bg-primary/10 p-4 text-sm text-text">
          {origin === 'resume'
            ? 'Encontramos un pago que empezaste hace un rato y todavía no está confirmado. '
            : 'Todavía no nos llegó la confirmación del cobro. '}
          <strong>{DO_NOT_REPAY_MESSAGE}</strong>
        </div>
        <SubmitButton type="button" onClick={onVerify}>
          Verificar de nuevo
        </SubmitButton>
        <button type="button" onClick={onPayAgain} className={btn('ghost', 'md', 'w-full')}>
          Pagar con otra tarjeta
        </button>
      </div>
    </AuthShell>
  );
}
